/* ═══════════════════════════════════════════════════════════════════════════════════════════════════════════
   THE BUILDER'S CAMERA MOVES (v2e, Yonatan's render, 17 Oct 2026). Daniel: "Replace the builder views as well."

   Until v2e every builder view was a 2D crop of ONE pose, the open card under the artist's camera: Pictures, Inside
   top and Note were the same picture zoomed to different places. At v2e's 100° open that put the note and the inside
   top about 31° off square (2026-10-15-v2e-render/REPORT.md), so Daniel asked Yonatan for camera moves, and on 17 Oct
   he sent three: Pictures → Note (24 frames), Pictures → Inside top (24) and Note → Inside top (30). Each is a shading
   pass on white with a geometry file in card-geometry.json's schema, the camera keyed on every frame, and they chain
   (the end of one is the start of the next; Pictures is the last frame of card-open, geometry frame 0).

   THREE POSES. The card rests in one of them: 'pictures' (the open card, today's), 'note' or 'top' (the last frame of
   the move there). camMode() decides which: 'note' → note, 'top' → top, anything else → pictures. Changing pose plays
   the move between them, the CSS planes posed from its geometry on every video frame (the same rVFC lockstep as the
   opening), and the 2D crop camera flies from where it was to the new view's framing over the same frames, so the
   two cameras move as one. Going back plays a REVERSED ENCODE of the same clip (card-move-note-pictures.mp4 and the
   other two, made with ffmpeg -vf reverse on 17 Oct) against the same geometry read backwards: every move plays
   forward, so it never seeks, and the decoder streams the way the opening's does.

   AT REST off Pictures, applyFrame() is pointed at the rest pose (every caller that re-applies `cardFrame` after a
   resize gets the right card), and .pp-shade carries that pose's shading still (card-shade-note/top.webp, the clip's
   last frame as black + alpha, like card-shade-0.webp). The opening, the close, Preview and the envelope all start
   from the open card, so the moment any of them begins the card is put back on Pictures without a move.

   On by default on this branch; ?ppmoves=0 turns it off in this browser (today's crops on the v2e card), ?ppmoves=1
   back. Reduced motion changes pose without the clip. Loaded AFTER builder-inside.js, which it wraps.
   ═══════════════════════════════════════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* ⛔ SAFARI STOPS CALLING requestVideoFrameCallback PARTWAY THROUGH A CLIP (18 Oct; found in the builder's opening
     with ?ppopendebug=1 on Daniel's Safari 26.6: the clip ran to its end while the card sat still, then snapped).
     afterFrame(v, cb) asks for the next video frame AND watches on rAF: when no video-frame callback has come for
     100ms while the clip plays, it calls cb with the clip's currentTime and stops asking that element for frame
     callbacks (v._ppRvfcDead), so the move goes on at the display's rate. cb(t, exact): exact is true only for a real
     video-frame callback (mediaTime). The same helper is in configurator.js, card-moves.js, builder-envelope.js and
     rd-card3d.js: change all four or none. */
  function afterFrame(v, cb) {
    var fired = false, t0 = performance.now();
    var rv = !!v.requestVideoFrameCallback && !v._ppRvfcDead;
    if (rv) v.requestVideoFrameCallback(function (now, meta) { if (fired) return; fired = true; cb(meta.mediaTime, true); });
    var poll = function () {
      if (fired) return;
      if (!rv || performance.now() - t0 > 100) {
        if (rv && !v.paused) v._ppRvfcDead = true;
        fired = true; cb(v.currentTime, false); return;
      }
      requestAnimationFrame(poll);
    };
    requestAnimationFrame(poll);
  }
  window.PP = window.PP || {};
  const PP = window.PP;
  if (typeof PP.builderB !== 'function') return;

  const ON = (function () {
    try {
      const m = /[?&]ppmoves=([01])(?:&|$)/.exec(location.search);
      if (m) { if (m[1] === '0') localStorage.setItem('pp_ppmoves', '0'); else localStorage.removeItem('pp_ppmoves'); }
      return localStorage.getItem('pp_ppmoves') !== '0';
    } catch (e) { return true; }
  })();
  const U = window.PP_MOVES;
  if (!ON || !U) return;

  /* The moves, by "from>to". `geo` is the forward clip's geometry; `rev` reads it backwards (frame n-1-i), which is
     what the reversed encode shows. */
  const MOVES = {
    'pictures>note': { geo: 'pn', video: 'pictures-note', rev: false },
    'note>pictures': { geo: 'pn', video: 'note-pictures', rev: true },
    'pictures>top':  { geo: 'pt', video: 'pictures-top',  rev: false },
    'top>pictures':  { geo: 'pt', video: 'top-pictures',  rev: true },
    'note>top':      { geo: 'nt', video: 'note-top',      rev: false },
    'top>note':      { geo: 'nt', video: 'top-note',      rev: true },
  };
  // Where each pose rests: a geometry and its last frame.
  const REST = { note: 'pn', top: 'pt' };

  const poseFor = function (mode) { return mode === 'note' ? 'note' : mode === 'top' ? 'top' : 'pictures'; };
  const reduced = function () {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  };
  // A quiet ease: the clip's own camera already eases in and out (five-frame holds at both ends), so ours only
  // needs not to start or stop dead.
  const ease = function (u) { return u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2; };
  const lerp = function (a, b, u) { return a + (b - a) * u; };

  const GEO = {}, BLOB = {};
  let RUNS = 0;
  /* The move in flight, kept OUT of the component: Alpine wraps what it stores in reactive proxies, and a proxied
     <video> throws on pause(). The component carries only its id (_ccRun), which is all the rest needs. */
  let ACTIVE = null;
  const loadGeo = function (k) {
    if (!GEO[k]) {
      GEO[k] = fetch(U.geo[k]).then(function (r) { if (!r.ok) throw new Error('moves_geo_' + r.status); return r.json(); })
        .then(function (g) { GEO[k].value = g; return g; });
      GEO[k].catch(function () { delete GEO[k]; });
    }
    return GEO[k];
  };
  const geoNow = function (k) { return GEO[k] && GEO[k].value; };
  // Fetched whole and played from memory (Yonatan's preview does the same): a 24-frame clip that has to stream
  // stalls on its first frames, and those are the ones the eye is on.
  const loadVideo = function (url) {
    if (!BLOB[url]) {
      BLOB[url] = fetch(url).then(function (r) { if (!r.ok) throw new Error('moves_clip_' + r.status); return r.blob(); })
        .then(function (b) { BLOB[url].value = URL.createObjectURL(b); return BLOB[url].value; });
      BLOB[url].catch(function () { delete BLOB[url]; });
    }
    return BLOB[url];
  };

  /* ⭐ ONE <video> PER CLIP, loaded once (18 Oct). The moves used to share one element and swap its src on every
     move, so each click paid a fresh load, a wait for the first frame and a cold play() before anything moved: about
     1.3 s in Safari's engine, measured, against ~0.1 s in Chrome (Daniel: "a long wait between clicking note or inside
     top and the animation of the camera moving", and Look). Each clip now has its own element, made and loaded when
     the clips are prefetched, so a move only rewinds and plays a decoded clip. Six small clips, 24-30 frames each. */
  const moveVid = function (src) {
    const stage = document.querySelector('[data-pp-stage]');
    let v = null;
    document.querySelectorAll('[data-pp-movevid]').forEach(function (x) { if (x.dataset.src === src) v = x; });
    if (v || !stage) return v;
    v = document.createElement('video');
    v.className = 'pp-shadesrc';
    v.setAttribute('data-pp-movevid', '');
    v.setAttribute('aria-hidden', 'true');
    v.muted = true; v.playsInline = true; v.preload = 'auto';
    v.dataset.src = src;
    v.src = src;
    stage.appendChild(v);
    try { v.load(); } catch (e) { /* loads on play */ }
    return v;
  };

  const inner = PP.builderB;
  PP.builderB = function () {
    const c = inner.apply(this, arguments);
    const base = { applyFrame: c.applyFrame, init: c.init };

    Object.assign(c, {
      ccPose: 'pictures',
      _ccRun: null,

      // The rest pose's geometry and frame, or null on Pictures (or before its geometry is in).
      _ccRest(pose) {
        const k = REST[pose], g = k && geoNow(k);
        if (!g) return null;
        const n = Object.keys(g.camera.position).length;
        return { geom: g, frame: n - 1 };
      },

      // The crop camera as it is ON SCREEN this instant, mid-transition included: the computed transform of
      // .pp-zoom (translate3d(x, y, 0) scale(s) → matrix(s, 0, 0, s, x, y)). _camLive() reads the custom
      // properties, which jump to their new value the moment Alpine writes them; the transform is what is drawn.
      _ccCamNow() {
        const z = document.querySelector('[data-pp-camera]');
        if (!z) return null;
        const m = /matrix\(([^)]+)\)/.exec(getComputedStyle(z).transform || '');
        if (!m) return this._camLive();
        const v = m[1].split(',').map(parseFloat);
        return (v.length === 6 && v[0] > 0) ? { x: v[4], y: v[5], scale: v[0] } : this._camLive();
      },

      _ccShade(pose) {
        const el = document.querySelector('[data-pp-stage] .pp-shade');
        if (el && el.dataset.ccPose !== pose) {
          el.dataset.ccPose = pose;
          el.style.backgroundImage = 'url("' + (U.shade[pose] || U.shade.pictures) + '")';
        }
      },
      _ccMoving(on) {
        const vp = document.querySelector('.pp-viewport');
        if (vp) vp.classList.toggle('pp-viewport--moving', !!on);
      },

      // Straight to a pose, no clip: reduced motion, a clip that will not load, and the snap back to Pictures when
      // the opening or Preview's fold takes the card.
      _ccSnap(pose, apply) {
        this._ccStop();
        this.ccPose = pose;
        this._ccShade(pose);
        if (apply) {
          const r = this._ccRest(pose);
          if (r) base.applyFrame.call(this, r.frame, r.geom);
          else base.applyFrame.call(this, this.cardFrame);
        }
      },

      _ccStop() {
        const run = ACTIVE;
        this._ccRun = null;
        if (!run) return;
        ACTIVE = null;
        run.cancel = true;
        try { run.vid.pause(); } catch (e) { /* gone */ }
        if (run.cv) run.cv.style.display = 'none';
        this._ccMoving(false);
      },

      _ccIdle() {
        return this.stage === 'builder' && !this.opening && !this.closing && !this.cardClosed && !!this.geom &&
          !!this._openingDecided;
      },

      _ccGo(to) {
        if (this._ccRun) { if (ACTIVE) ACTIVE.next = to; return; }
        const from = this.ccPose;
        if (from === to || !this._ccIdle()) return;
        const mv = MOVES[from + '>' + to];
        if (!mv) return;
        if (reduced()) { this._ccSnapAndFrame(to); return; }
        const self = this;
        const sm = window.matchMedia && window.matchMedia('(max-width: 700px)').matches;
        const url = U.clip[mv.video + (sm ? '-sm' : '')];
        // The clip has 700 ms to be in; past that the pose changes without it, and the next move will have it.
        let timedOut = false;
        const late = setTimeout(function () { timedOut = true; if (self.ccPose === from) self._ccSnapAndFrame(to); }, 700);
        Promise.all([loadGeo(mv.geo), loadVideo(url)]).then(function (res) {
          clearTimeout(late);
          if (timedOut || self.ccPose !== from || self._ccRun) return;
          if (poseFor(self.camMode()) !== to || !self._ccIdle()) return;
          self._ccPlay(from, to, mv, res[0], res[1]);
        }).catch(function () { clearTimeout(late); if (!timedOut) self._ccSnapAndFrame(to); });
      },

      // Snap and let today's camera glide take it there.
      _ccSnapAndFrame(to) {
        this._ccSnap(to, true);
      },

      _ccPlay(from, to, mv, geom, src) {
        const self = this, CS = PP.CardStage;
        const stage = document.querySelector('[data-pp-stage]');
        if (!CS || !stage) return this._ccSnapAndFrame(to);
        const n = Object.keys(geom.camera.position).length;
        const gFrame = function (i) { return mv.rev ? n - 1 - i : i; };

        // Where the crop camera ends: the new view's framing of the new pose's boxes. frameBoxes is written for one
        // synchronous moment to ask cameraTarget() (camMode() is already the new mode), and the move then drives the
        // camera by hand from where it is now to there. At the end applyFrame publishes the same boxes, so the hand-
        // back lands on exactly this value.
        const startCam = this._ccCamNow() || this.cameraTarget();
        this._camDrive(startCam);
        const endT = CS.frameTransforms(geom, gFrame(n - 1));
        const endBoxes = {};
        Object.keys(endT.planes).forEach(function (k) { if (endT.planes[k].facing) endBoxes[k] = endT.planes[k].box; });
        this.frameBoxes = endBoxes;
        const endCam = this.cameraTarget();

        // The shading, multiplied OUTSIDE .pp-zoom like the opening's (video has no alpha), so the box is written per
        // frame from the camera we are driving: the two move together because one function writes both.
        let cv = document.querySelector('[data-pp-movecanvas]');
        if (!cv) {
          cv = document.createElement('canvas');
          cv.className = 'pp-shadevid';
          cv.setAttribute('data-pp-movecanvas', '');
          cv.setAttribute('aria-hidden', 'true');
          stage.appendChild(cv);
        }
        const vid = moveVid(src);
        if (!vid) return this._ccSnapAndFrame(to);
        const comp = geom.comp.width / (CS.LAYOUT_SCALE || 1);
        const dpr = window.devicePixelRatio || 1;
        const maxScale = Math.max(startCam.scale, endCam.scale);
        const px = Math.min(2048, Math.round(comp * maxScale * dpr));
        if (cv.width !== px) { cv.width = px; cv.height = px; }
        const cx = cv.getContext('2d', { alpha: true });
        if (cx) { cx.imageSmoothingEnabled = true; cx.imageSmoothingQuality = 'high'; }
        const W = stage.clientWidth, H = stage.clientHeight;
        const place = function (cam) {
          const size = comp * cam.scale;
          cv.style.cssText = 'display:block;animation:none;width:' + size.toFixed(1) + 'px;height:' + size.toFixed(1) + 'px;' +
            'left:' + (W / 2 + cam.x - size / 2).toFixed(1) + 'px;top:' + (H / 2 + cam.y - size / 2).toFixed(1) + 'px;';
        };

        // Compared by id, never by identity: Alpine hands back a reactive proxy of whatever is stored on the component.
        const run = { id: ++RUNS, cancel: false, next: null, vid: vid, cv: cv };
        ACTIVE = run;
        this._ccRun = run.id;
        let last = -1;
        const frame = function (i) {
          if (i === last) return;
          last = i;
          base.applyFrame.call(self, gFrame(i), geom, true);
          const u = ease(i / (n - 1));
          const cam = { x: lerp(startCam.x, endCam.x, u), y: lerp(startCam.y, endCam.y, u),
                        scale: startCam.scale * Math.pow(endCam.scale / startCam.scale, u) };
          self._camDrive(cam);
          place(cam);
          if (cx && vid.videoWidth) { cx.clearRect(0, 0, cv.width, cv.height); cx.drawImage(vid, 0, 0, cv.width, cv.height); }
        };
        const finish = function () {
          if (run.cancel) return;
          self._ccRun = null;
          ACTIVE = null;
          self.ccPose = to;
          self._ccShade(to);
          if (to === 'pictures') base.applyFrame.call(self, self.cardFrame);
          else base.applyFrame.call(self, gFrame(n - 1), geom);
          self._camRelease();
          // The canvas leaves a frame after the still is back, so there is never a frame with no shading at all.
          requestAnimationFrame(function () { cv.style.display = 'none'; self._ccMoving(false); });
          const want = run.next || poseFor(self.camMode());
          if (want !== to) setTimeout(function () { self._ccGo(want); }, 0);
        };
        /* rVFC for the lockstep, and a timer under it: a video whose frame callbacks stop (a hidden or throttled tab,
           a capture in progress) would otherwise leave the card half-moved for good. The timer reads currentTime,
           so the card still lands where the clip is. Yonatan's preview does the same. */
        let seq = 0;
        const schedule = function () {
          const s = ++seq;
          afterFrame(vid, function (t, exact) { if (s === seq) step(null, exact ? { mediaTime: t } : undefined); });
          setTimeout(function () { if (s === seq) step(); }, 250);   // a hidden tab: rAF stops too
        };
        const step = function (now, meta) {
          seq++;
          if (run.cancel) return;
          if (!self._ccIdle()) { self._ccSnap('pictures', false); return; }
          const t = meta ? meta.mediaTime : vid.currentTime;
          const i = Math.min(n - 1, Math.max(0, Math.round(t * 30)));
          frame(i);
          if (i >= n - 1 || vid.ended || (vid.paused && !meta)) { frame(n - 1); finish(); return; }
          schedule();
        };
        const go = function () {
          if (run.cancel) return;
          try { vid.currentTime = 0; } catch (e) { /* at 0 already */ }
          self._ccMoving(true);
          frame(0);
          const p = vid.play();
          schedule();
          // A tab that will not play (background, power saving) still gets the card where it was asked to go.
          if (p && p.catch) p.catch(function () { if (!run.cancel) { self._ccStop(); self._ccSnapAndFrame(to); self._camRelease(); } });
        };
        if (vid.readyState >= 2) { go(); return; }
        /* A clip that never decodes (no H.264 in the engine, a bad file) must not hold the card: 600 ms, or an error,
           and the pose changes without it. */
        const bail = function () {
          vid.removeEventListener('loadeddata', ready);
          if (run.cancel || self._ccRun !== run.id) return;
          self._ccStop();
          self._ccSnapAndFrame(to);
          self._camRelease();
        };
        const ready = function () { clearTimeout(wait); vid.removeEventListener('error', bail); go(); };
        const wait = setTimeout(bail, 600);
        vid.addEventListener('loadeddata', ready, { once: true });
        vid.addEventListener('error', function () { clearTimeout(wait); bail(); }, { once: true });
      },

      // Every caller that re-applies the card's frame gets the pose the card is resting in.
      applyFrame(frame, geomOverride, quiet) {
        if (!geomOverride && this.ccPose !== 'pictures' && !this._ccRun && this._ccIdle()) {
          const r = this._ccRest(this.ccPose);
          if (r) return base.applyFrame.call(this, r.frame, r.geom, quiet);
        }
        return base.applyFrame.apply(this, arguments);
      },

      init() {
        const out = base.init.apply(this, arguments);
        const self = this;
        // Which pose the view wants.
        this.$watch(function () { return poseFor(self.camMode()); }, function (to) {
          if (self._ccIdle()) self._ccGo(to);
        });
        // The opening, the close, Preview's fold and the envelope all begin from the open card: Pictures, at once.
        this.$watch(function () { return !!(self.opening || self.closing || self.cardClosed) || self.stage !== 'builder'; },
          function (busy) {
            if (busy && (self.ccPose !== 'pictures' || self._ccRun)) self._ccSnap('pictures', false);
            else if (!busy) { const want = poseFor(self.camMode()); if (want !== self.ccPose) self._ccGo(want); }
          });
        // Everything in once the card has opened, the small clips first: the first move should never wait.
        this.$watch(function () { return !!self._openingDecided && !self.opening; }, function (ready) {
          if (!ready || self._ccPrefetched) return;
          self._ccPrefetched = true;
          const sm = window.matchMedia && window.matchMedia('(max-width: 700px)').matches;
          setTimeout(function () {
            let p = Promise.resolve();
            ['pn', 'pt', 'nt'].forEach(function (k) { p = p.then(function () { return loadGeo(k); }).catch(function () {}); });
            Object.keys(MOVES).forEach(function (k) {
              p = p.then(function () { return loadVideo(U.clip[MOVES[k].video + (sm ? '-sm' : '')]); })
                .then(function (u) { if (u) moveVid(u); }).catch(function () {});
            });
            [U.shade.note, U.shade.top].forEach(function (s) { const im = new Image(); im.src = s; });
          }, 1200);
        });
        return out;
      },
    });
    return c;
  };
})();
